import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icone, { NomeIcone } from '../primitivos/Icone';

/**
 * Casco das telas do SAAC (at-55, frente A).
 *
 * Substitui, para as telas migradas, a composição `.main-containt` + `NavGlobal`.
 *
 * ## Navegação: UM painel, todas as larguras (revisto por Victor em 08/09/2026)
 *
 * A primeira versão tinha **duas** navegações: uma sidebar fixa no desktop (que
 * empurrava o conteúdo) e um drawer no mobile. Victor pediu a sidebar **fechável
 * com clique fora** — e, com isso, o desenho certo passou a ser um só: um painel
 * que **sobrepõe**, com fundo escurecido, fechando por clique fora, `Esc` ou ao
 * escolher uma opção.
 *
 * ⚠️ Foi essa escolha que tornou possível **unificar**. Com a sidebar no fluxo,
 * "clicar fora" seria clicar no conteúdo — e o menu se fecharia a cada interação
 * com a tela. Sobrepondo, quando o painel está aberto ninguém está trabalhando no
 * conteúdo, e o gesto de fechar é natural. De quebra, o conteúdo ganha 100% da
 * largura e some a duplicação de markup entre sidebar e drawer.
 *
 * ## Modos visuais que permanecem (Q2)
 *
 *   • **desktop (≥1024px)** — fundo neutro; o laranja da marca é ACENTO.
 *   • **mobile (<1024px)** — fundo laranja, gatilho dentro da faixa do topo.
 *
 * ⚠️ **Componente PURO**: não conhece Redux, rotas nem `menuOpcoes` — nem o
 * arquivo da logo. Tudo entra por prop. É essa regra que mantém `src/ui`
 * reutilizável; a ponte com o domínio é `src/components/shell/ShellSaac.tsx`.
 *
 * ⚠️ **Altura**: enquanto houver tela legada, o desconto do `PendenciasBanner`
 * continua vindo de `--altura-banner` (publicada por ele no `:root`). O banner é
 * irmão deste shell no `App`, não filho — por isso o `calc`. Quando TODAS as
 * telas migrarem (frente D), o banner pode entrar aqui e o `calc` vira flex.
 * O que este shell já resolve é o outro sintoma: o gatilho do menu vive DENTRO
 * dele, então nunca mais fica escondido atrás da faixa (bug real de 03/09/2026).
 */

export interface ItemNavegacao {
  id: string;
  titulo: string;
  icone: NomeIcone;
  ativo: boolean;
  aoSelecionar: () => void;
}

interface PropsAppShell {
  itens: ItemNavegacao[];
  inicioAtivo: boolean;
  aoIrParaInicio: () => void;
  aoSair: () => void;
  usuario?: { nome?: string | null; papel?: string | null };
  /**
   * Marca exibida no topo do painel. Entra por prop para o shell não conhecer os
   * assets do SAAC — ver a regra de camada acima.
   */
  marca?: React.ReactNode;
  /** Cabeçalho da tela (normalmente um `<PageHeader>`). */
  cabecalho?: React.ReactNode;
  /**
   * Ações que valem em TODA tela (hoje: o sino de novidades), injetadas no
   * cabeçalho junto do gatilho do menu.
   *
   * ⚠️ Entram por prop, como a `marca`: o shell não pode conhecer o sino, que é
   * componente de domínio (lê API e Redux). Quem o monta é o `ShellSaac`.
   *
   * 🔴 Existem porque uma ação global **não pode flutuar por cima da ação da
   * tela** — era o que acontecia com o sino `position: fixed` sobre o
   * "Nova Adesão +" de `/adesoes` (at-56).
   */
  acoesGlobais?: React.ReactNode;
  children: React.ReactNode;
}

const AppShell: React.FC<PropsAppShell> = ({
  itens,
  inicioAtivo,
  aoIrParaInicio,
  aoSair,
  usuario,
  marca,
  cabecalho,
  acoesGlobais,
  children,
}) => {
  const [menuAberto, setMenuAberto] = useState(false);
  const gatilhoRef = useRef<HTMLButtonElement>(null);
  const fecharRef = useRef<HTMLButtonElement>(null);
  // Só devolve o foco ao gatilho se o painel chegou a abrir — sem isso o efeito
  // roubaria o foco em todo carregamento de página (armadilha do NavGlobal).
  const jaAbriu = useRef(false);

  const fechar = useCallback(() => setMenuAberto(false), []);

  // Esc fecha. No `document` porque o foco pode estar em qualquer item.
  useEffect(() => {
    if (!menuAberto) return undefined;
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') fechar();
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [menuAberto, fechar]);

  // Foco: entra no painel ao abrir; volta ao gatilho ao fechar, mas só se tiver
  // ficado órfão (não roubar o foco de onde o usuário já está).
  useEffect(() => {
    if (menuAberto) {
      jaAbriu.current = true;
      fecharRef.current?.focus();
      return;
    }
    if (jaAbriu.current && document.activeElement === document.body) {
      gatilhoRef.current?.focus();
    }
  }, [menuAberto]);

  const navegar = (item: ItemNavegacao) => {
    item.aoSelecionar();
    fechar();
  };

  const gatilhoMenu = (
    <button
      type="button"
      ref={gatilhoRef}
      onClick={() => setMenuAberto(true)}
      aria-label="Abrir menu de navegação"
      aria-expanded={menuAberto}
      aria-controls="app-shell-menu"
      /*
       * Duas peles: sobre o laranja do mobile é translúcido; na faixa BRANCA do
       * desktop precisa de contorno, senão um fundo quase branco desaparece.
       *
       * 📌 `lg:tw-border` sobre `tw-border-0` é seguro: o Tailwind emite as
       * variantes responsivas DEPOIS das classes base, então em ≥lg a borda vence.
       * (Não vale para dois utilitários do MESMO breakpoint — ver `Card.tsx`.)
       */
      className="print:tw-hidden tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-white/15 tw-text-white hover:tw-bg-white/25 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-white/60 lg:tw-border lg:tw-border-superficie-borda lg:tw-bg-white lg:tw-text-texto-corpo lg:hover:tw-border-brand/40 lg:hover:tw-bg-brand-tint lg:hover:tw-text-brand lg:focus-visible:tw-ring-brand/40"
    >
      <Icone nome="menu" tamanho={20} traco={2.5} />
    </button>
  );

  return (
    <div
      /*
       * 🔴 Marca a subárvore do design system. `ui/tokens/base.css` usa este
       * atributo para repor a única parte do `preflight` sem a qual os
       * utilitários de borda do Tailwind não funcionam (`border-style`) — ver o
       * comentário lá. Sem ele, `tw-border` e `tw-border-b` não desenham nada em
       * `<div>`, e a borda de `<input>` sai com o estilo `inset` do navegador.
       */
      data-design-system=""
      className="tw-flex tw-w-full tw-overflow-hidden"
      style={{ height: 'calc(100vh - var(--altura-banner, 0px))' }}
    >
      {/*
        ───────── PAINEL DE NAVEGAÇÃO (todas as larguras) ─────────
        🔴 O backdrop É o clique-fora: ele cobre tudo o que não é o painel, então
        clicar em qualquer ponto do conteúdo cai aqui. É mais simples e mais
        acessível que um listener de `mousedown` no document — e não tem o risco
        clássico de fechar no `mousedown` que começou DENTRO do painel.
      */}
      {menuAberto && (
        <div
          className="print:tw-hidden tw-fixed tw-inset-0 tw-z-[1000] tw-bg-black/40 tw-transition-opacity motion-reduce:tw-transition-none"
          onClick={fechar}
          role="presentation"
        />
      )}

      {/*
        🔴 **`print:tw-hidden` no painel, no backdrop e no gatilho** (at-64).

        A prévia do laudo é a **única** tela do app que imprime (`window.print()`),
        e o shell não sabia nada sobre isso: o painel de navegação fica no DOM
        mesmo fechado (é `translate-x-full`, não desmontado), então saía na folha.
        Antes, quem escondia os elementos do cabeçalho na impressão era um
        `@media print` dentro do CSS daquela tela — que sai nesta branch.

        ⚠️ A regra é **propriedade do shell**, não da tela: qualquer tela migrada
        que venha a imprimir herda o comportamento certo. É por isso que ela nasce
        aqui com um consumidor só.
      */}
      <aside
        id="app-shell-menu"
        role="dialog"
        aria-modal="true"
        aria-label="Menu de navegação"
        aria-hidden={!menuAberto}
        className={[
          'print:tw-hidden',
          'tw-fixed tw-inset-y-0 tw-left-0 tw-z-[1001] tw-flex tw-w-72 tw-flex-col tw-bg-sidebar tw-shadow-2xl',
          'tw-transition-transform tw-duration-200 motion-reduce:tw-transition-none',
          menuAberto ? 'tw-translate-x-0' : '-tw-translate-x-full',
        ].join(' ')}
      >
        <div className="tw-flex tw-items-center tw-gap-3 tw-border-b tw-border-white/10 tw-px-5 tw-py-4">
          {marca}
          <button
            type="button"
            ref={fecharRef}
            onClick={fechar}
            aria-label="Fechar menu"
            tabIndex={menuAberto ? 0 : -1}
            className="tw-ml-auto tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-transparent tw-text-white/70 hover:tw-bg-white/10 hover:tw-text-white focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-white/60"
          >
            <Icone nome="fechar" tamanho={18} />
          </button>
        </div>

        {usuario?.nome && (
          <div className="tw-border-b tw-border-white/10 tw-px-5 tw-py-4">
            <p className="tw-m-0 tw-truncate tw-font-marca tw-text-sm tw-font-bold tw-text-white">
              {usuario.nome}
            </p>
            {usuario.papel && (
              <span className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-brand">
                {usuario.papel}
              </span>
            )}
          </div>
        )}

        {/*
          A barra de rolagem do painel **nunca aparece** (pedido de Victor,
          09/09/2026). A rolagem continua funcionando por toque e roda — só o
          desenho da barra sai, que é o padrão de painel de navegação.

          ⚠️ Precisa dos dois: `scrollbar-width` cobre Firefox e o
          `::-webkit-scrollbar` cobre Chrome/Safari. Esconder só num dos dois
          deixa o defeito visível na metade dos navegadores.

          ⚠️ E `overflow-y-auto` **fica**: com 9 itens de menu o conteúdo estoura
          em tela curta (foi o que fez a barra surgir), e trocar por `hidden`
          tornaria as últimas opções inalcançáveis — trocaria um arranhão visual
          por perda de função.
        */}
        <nav className="tw-flex tw-flex-1 tw-flex-col tw-overflow-y-auto tw-py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:tw-hidden">
          <button
            type="button"
            onClick={() => {
              aoIrParaInicio();
              fechar();
            }}
            tabIndex={menuAberto ? 0 : -1}
            aria-current={inicioAtivo ? 'page' : undefined}
            className={itemClasses(inicioAtivo)}
          >
            <Icone nome="inicio" tamanho={18} />
            <span className="tw-flex-1 tw-text-left">Início</span>
            {inicioAtivo && <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-brand" />}
          </button>

          <div className="tw-mx-4 tw-my-2 tw-h-px tw-bg-white/10" />

          {itens.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => navegar(item)}
              tabIndex={menuAberto ? 0 : -1}
              aria-current={item.ativo ? 'page' : undefined}
              className={itemClasses(item.ativo)}
            >
              <Icone nome={item.icone} tamanho={18} />
              <span className="tw-flex-1 tw-text-left">{item.titulo}</span>
              {item.ativo && <span className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-brand" />}
            </button>
          ))}
        </nav>

        <div className="tw-border-t tw-border-white/10 tw-p-3">
          <button
            type="button"
            onClick={aoSair}
            tabIndex={menuAberto ? 0 : -1}
            className={itemClasses(false)}
          >
            <Icone nome="sair" tamanho={18} />
            <span className="tw-flex-1 tw-text-left">Sair</span>
          </button>
        </div>
      </aside>

      {/* ───────── CONTEÚDO (100% da largura) ───────── */}
      <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-bg-brand lg:tw-bg-shell">
        {React.isValidElement(cabecalho) ? (
          React.cloneElement(
            cabecalho as React.ReactElement<{
              gatilhoMenu?: React.ReactNode;
              acoesGlobais?: React.ReactNode;
            }>,
            { gatilhoMenu, acoesGlobais },
          )
        ) : (
          /* ⚠️ Sem cabeçalho o gatilho precisa aparecer mesmo assim — senão a
             tela ficaria sem NENHUM acesso ao menu. E as ações globais também,
             senão o sino desapareceria numa tela sem `PageHeader`. */
          <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3">
            {gatilhoMenu}
            {acoesGlobais}
          </div>
        )}

        {/* A rolagem do conteúdo é travada enquanto o painel está aberto. Não dá
            para usar a classe legada `nav-global-aberto`: ela trava
            `.main-containt`, que o shell não usa. */}
        <main className={menuAberto ? 'tw-flex-1 tw-overflow-hidden' : 'tw-flex-1 tw-overflow-y-auto'}>
          {children}
        </main>
      </div>
    </div>
  );
};

function itemClasses(ativo: boolean): string {
  return [
    'tw-flex tw-w-full tw-items-center tw-gap-3 tw-border-0 tw-bg-transparent tw-px-4 tw-py-2.5',
    'tw-text-sm tw-font-semibold tw-cursor-pointer tw-transition-colors motion-reduce:tw-transition-none',
    'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-inset focus-visible:tw-ring-brand/50',
    ativo ? 'tw-bg-white/10 tw-text-white' : 'tw-text-white/50 hover:tw-bg-white/5 hover:tw-text-white/90',
  ].join(' ');
}

export default AppShell;
